import React, { useState, useMemo, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { Button, Input, Select } from '../components/ui/Forms';
import { Modal } from '../components/ui/Modal';
import SafeIcon from '../common/SafeIcon';
import { getNextDueDate, formatDate, calculateTotalOccurrences } from '../utils/dateUtils';
import { cn } from '../utils/cn';
import { SUPPORTED_CURRENCIES, formatCurrency, convertCurrency } from '../utils/currencyUtils';

const INTERVALS = [
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Fortnightly', value: 'Fortnightly' },
  { label: '28 Days', value: '28 Days' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Annual', value: 'Annual' },
  { label: 'Every Two Years', value: 'Every Two Years' },
];

export default function Subscriptions() {
  const { state, addSubscription, updateSubscription, deleteSubscription, cancelSubscription, unCancelSubscription } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const fileInputRef = useRef(null);

  const initialFormState = {
    name: '',
    planName: '',
    description: '',
    loginUrl: '',
    logo: '',
    currency: state.settings.preferredCurrency || 'USD',
    recurrenceInterval: 'Monthly',
    commencementDate: new Date().toISOString().split('T')[0],
    recurringAmount: '',
    upfrontPayment: '',
    aiCreditEnabled: false,
    aiCreditAmount: '',
    aiCreditRecurrenceInterval: 'Monthly',
    paymentMethodId: '',
    categoryId: '',
    status: 'active',
    cancelledAt: null
  };

  const [formData, setFormData] = useState(initialFormState);

  const filteredSubscriptions = useMemo(() => {
    return state.subscriptions.filter(sub => {
      const subStatus = sub.status || 'active';
      const matchCat = categoryFilter === 'all' || sub.categoryId === categoryFilter;
      const matchStatus = statusFilter === 'all' || subStatus === statusFilter;
      return matchCat && matchStatus;
    });
  }, [state.subscriptions, categoryFilter, statusFilter]);

  const handleEdit = (sub) => {
    setFormData({ ...sub });
    setEditingId(sub.id);
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissionData = {
      ...formData,
      recurringAmount: parseFloat(formData.recurringAmount) || 0,
      upfrontPayment: parseFloat(formData.upfrontPayment) || 0,
      aiCreditAmount: formData.aiCreditEnabled ? (parseFloat(formData.aiCreditAmount) || 0) : null
    };
    if (editingId) {
      updateSubscription(editingId, submissionData);
    } else {
      addSubscription(submissionData);
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(initialFormState);
  };

  const getCategoryIcon = (id) => {
    const cat = state.categories.find(c => c.id === id);
    return cat ? cat.icon : 'Tag';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Your Subscriptions</h1>
          <p className="text-slate-500">Track and manage all your recurring services.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[140px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-teal-500 appearance-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="cancelled">Cancelled Only</option>
            </select>
            <SafeIcon name="ChevronDown" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          <div className="relative min-w-[160px]">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-teal-500 appearance-none"
            >
              <option value="all">All Categories</option>
              {state.categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <SafeIcon name="ChevronDown" className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          <Button onClick={() => setIsModalOpen(true)}>
            <SafeIcon name="Plus" /> New Sub
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSubscriptions.map((sub) => {
          const subStatus = sub.status || 'active';
          const nextDue = subStatus === 'active' ? getNextDueDate(sub.commencementDate, sub.recurrenceInterval) : null;
          const nextCreditReset = sub.aiCreditEnabled ? getNextDueDate(sub.commencementDate, sub.aiCreditRecurrenceInterval) : null;
          const isCancelled = subStatus === 'cancelled';
          
          // Payments Count (Based on Calendar Data)
          const paymentsCount = calculateTotalOccurrences(sub.commencementDate, sub.recurrenceInterval, sub.cancelledAt);
          
          // Total Spent (Using historical count * amount)
          const totalSpentConverted = paymentsCount * convertCurrency(
            sub.recurringAmount || 0,
            sub.currency || 'USD',
            state.settings.preferredCurrency || 'USD',
            state.exchangeRates
          );

          const convertedAmount = convertCurrency(
            sub.recurringAmount || 0,
            sub.currency || 'USD',
            state.settings.preferredCurrency || 'USD',
            state.exchangeRates
          );

          return (
            <div key={sub.id} className={cn(
              "bg-white p-6 rounded-3xl shadow-sm border transition-all group relative overflow-hidden flex flex-col justify-between",
              isCancelled ? "border-slate-100 bg-slate-50/40 opacity-80" : "border-slate-100 hover:shadow-md"
            )}>
              {isCancelled && (
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-200 text-slate-600 border border-slate-300 z-10">
                  Cancelled
                </div>
              )}
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {sub.loginUrl ? (
                        <a href={sub.loginUrl} target="_blank" rel="noopener noreferrer" className="block transition-transform hover:scale-105 active:scale-95">
                          {sub.logo ? (
                            <img src={sub.logo} alt="" className="w-14 h-14 rounded-2xl object-cover bg-slate-50 border border-slate-100 shadow-sm" />
                          ) : (
                            <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center border border-teal-100">
                              <SafeIcon name="ExternalLink" className="w-6 h-6" />
                            </div>
                          )}
                        </a>
                      ) : (
                        sub.logo ? (
                          <img src={sub.logo} alt="" className="w-14 h-14 rounded-2xl object-cover bg-slate-50 border border-slate-100 shadow-sm" />
                        ) : (
                          <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center border border-teal-100">
                            <SafeIcon name="Zap" className="w-7 h-7" />
                          </div>
                        )
                      )}
                      {/* Category Icon Overlay */}
                      <div className="absolute -bottom-1 -right-1 p-1.5 bg-white rounded-lg shadow-sm border border-slate-100 text-slate-500">
                        <SafeIcon name={getCategoryIcon(sub.categoryId)} className="w-3 h-3" />
                      </div>
                    </div>
                    <div className="overflow-hidden">
                      <h3 className="font-bold text-slate-800 leading-tight truncate">{sub.name}</h3>
                      <p className="text-xs text-slate-500 truncate font-medium">{sub.planName || 'Standard Plan'}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEdit(sub)} className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Edit Subscription">
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </button>
                    <button onClick={() => { if (window.confirm('Permanently delete this subscription and all its data?')) deleteSubscription(sub.id); }} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-black text-slate-900 tracking-tight">
                      {formatCurrency(convertedAmount, state.settings.preferredCurrency || 'USD')}
                    </span>
                    <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">/ {(sub.recurrenceInterval || 'Monthly').toLowerCase()}</span>
                  </div>
                </div>

                {/* AI Credits Badge */}
                {sub.aiCreditEnabled && (
                  <div className="mb-4 p-2.5 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <SafeIcon name="Cpu" className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-bold text-purple-700">{sub.aiCreditAmount} Credits</span>
                    </div>
                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-tighter">Resets {formatDate(nextCreditReset)}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-slate-50/80 p-2.5 rounded-2xl border border-slate-100">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Payments</p>
                    <p className="text-sm font-bold text-slate-700">{paymentsCount} Cycles</p>
                  </div>
                  <div className="bg-slate-50/80 p-2.5 rounded-2xl border border-slate-100">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Total Spend</p>
                    <p className="text-sm font-bold text-slate-700">{formatCurrency(totalSpentConverted, state.settings.preferredCurrency)}</p>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t border-slate-100 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 font-medium flex items-center gap-2 uppercase tracking-widest text-[9px]">
                      <SafeIcon name="Calendar" className="w-3.5 h-3.5" /> {isCancelled ? 'Cancelled On' : 'Next Billing'}
                    </span>
                    <span className={cn("font-bold", isCancelled ? "text-slate-500" : "text-teal-600")}>
                      {isCancelled ? formatDate(sub.cancelledAt) : formatDate(nextDue)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                {!isCancelled ? (
                  <button onClick={() => { if (window.confirm(`Cancel ${sub.name}? Payment history is preserved for your analytics.`)) { cancelSubscription(sub.id); } }} className="flex-1 py-2.5 rounded-xl text-[10px] font-bold text-red-600 border border-red-100 bg-red-50/30 hover:bg-red-50 transition-all flex items-center justify-center gap-1.5 uppercase tracking-widest" >
                    <SafeIcon name="XCircle" className="w-3.5 h-3.5" /> Stop Billing
                  </button>
                ) : (
                  <button onClick={() => { if (window.confirm(`Reactivate ${sub.name}? This will resume tracking upcoming payments.`)) { unCancelSubscription(sub.id); } }} className="flex-1 py-2.5 rounded-xl text-[10px] font-bold text-teal-600 border border-teal-100 bg-teal-50/30 hover:bg-teal-50 transition-all flex items-center justify-center gap-1.5 uppercase tracking-widest" >
                    <SafeIcon name="RefreshCw" className="w-3.5 h-3.5" /> Reactivate
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingId ? "Edit Subscription" : "Add Subscription"}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Service Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required placeholder="e.g. Midjourney" />
            <Input label="Plan Details" value={formData.planName} onChange={e => setFormData({ ...formData, planName: e.target.value })} placeholder="e.g. Pro Plan" />
            
            <div className="md:col-span-2">
              <Input label="Login Page URL" value={formData.loginUrl} onChange={e => setFormData({ ...formData, loginUrl: e.target.value })} placeholder="https://app.midjourney.com" />
            </div>

            <div className="md:col-span-2 space-y-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">Logo Management</label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input value={formData.logo} onChange={e => setFormData({ ...formData, logo: e.target.value })} placeholder="Logo URL (e.g. clearbit.com)" />
                  </div>
                  <div className="flex-shrink-0">
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                    <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()} className="h-full px-4 rounded-xl">
                      <SafeIcon name="Upload" className="w-4 h-4" /> Upload
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <Select 
                  label="Currency" 
                  value={formData.currency} 
                  onChange={e => setFormData({ ...formData, currency: e.target.value })}
                  options={SUPPORTED_CURRENCIES.map(c => ({ label: `${c.code} (${c.symbol})`, value: c.code }))}
                  required
                />
              </div>
              <div className="col-span-2">
                <Input label="Recurring Amount" type="number" step="0.01" value={formData.recurringAmount} onChange={e => setFormData({ ...formData, recurringAmount: e.target.value })} required />
              </div>
            </div>

            <Select label="Billing Interval" value={formData.recurrenceInterval} onChange={e => setFormData({ ...formData, recurrenceInterval: e.target.value })} options={INTERVALS} required />
            <Input label="Commencement Date" type="date" value={formData.commencementDate} onChange={e => setFormData({ ...formData, commencementDate: e.target.value })} required />
            
            <Select label="Category" value={formData.categoryId} onChange={e => setFormData({ ...formData, categoryId: e.target.value })} required options={state.categories.map(c => ({ label: c.name, value: c.id }))} />
            <Select label="Payment Method" value={formData.paymentMethodId} onChange={e => setFormData({ ...formData, paymentMethodId: e.target.value })} required options={state.paymentMethods.map(p => ({ label: `${p.nickname} (*${p.last4})`, value: p.id }))} />
            
            <div className="md:col-span-2 p-5 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SafeIcon name="Cpu" className="text-purple-600" />
                  <span className="font-bold text-slate-700">AI Credits Management</span>
                </div>
                <button 
                  type="button"
                  onClick={() => setFormData({ ...formData, aiCreditEnabled: !formData.aiCreditEnabled })}
                  className={cn(
                    "w-12 h-6 rounded-full transition-all relative",
                    formData.aiCreditEnabled ? "bg-purple-600" : "bg-slate-300"
                  )}
                >
                  <div className={cn("absolute top-1 w-4 h-4 bg-white rounded-full transition-all", formData.aiCreditEnabled ? "left-7" : "left-1")} />
                </button>
              </div>
              
              {formData.aiCreditEnabled && (
                <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                  <Input label="Credit Amount" type="number" value={formData.aiCreditAmount} onChange={e => setFormData({ ...formData, aiCreditAmount: e.target.value })} required />
                  <Select label="Reset Cycle" value={formData.aiCreditRecurrenceInterval} onChange={e => setFormData({ ...formData, aiCreditRecurrenceInterval: e.target.value })} options={INTERVALS} required />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" className="px-8">{editingId ? 'Update' : 'Save'} Subscription</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}